import { useEffect, useCallback, useState } from 'react';
import { useApplicationStore } from '../stores/applicationStore';
import { useAuthStore } from '../stores/authStore';
import { resendEmailService } from '../services/resendEmailService';
import { differenceInDays, addDays, startOfWeek, endOfWeek, format } from 'date-fns';
import { it } from 'date-fns/locale';

export const useApplicationReminders = () => {
  const { applications } = useApplicationStore();
  const { userProfile } = useAuthStore();
  const [lastReminderCheck, setLastReminderCheck] = useState<Date>(new Date());
  const [remindersSent, setRemindersSent] = useState<Set<string>>(new Set());

  // Controlla se una candidatura ha bisogno di reminder
  const needsReminder = useCallback((application: any) => {
    if (!application.appliedDate || application.status !== 'In attesa') {
      return false;
    }

    const appliedDate = application.appliedDate.toDate ? 
      application.appliedDate.toDate() : 
      new Date(application.appliedDate);
    
    const daysSinceApplication = differenceInDays(new Date(), appliedDate);
    
    // Reminder dopo 7 giorni, ma non più di una volta
    const reminderKey = `${application.id}_reminder`;
    if (remindersSent.has(reminderKey)) {
      return false;
    }
    
    return daysSinceApplication >= 7 && daysSinceApplication <= 10;
  }, [remindersSent]);

  // Controlla se una candidatura ha bisogno di reminder colloquio
  const needsInterviewReminder = useCallback((application: any) => {
    if (!application.interviewDate || application.status !== 'Colloquio programmato') {
      return false;
    }

    const interviewDate = application.interviewDate.toDate ? 
      application.interviewDate.toDate() : 
      new Date(application.interviewDate);
    
    const now = new Date();
    const hoursUntilInterview = (interviewDate.getTime() - now.getTime()) / (1000 * 60 * 60);
    
    // Reminder 24h e 1h prima del colloquio
    const reminderKey24h = `${application.id}_interview_24h`;
    const reminderKey1h = `${application.id}_interview_1h`;
    
    if (hoursUntilInterview <= 1 && hoursUntilInterview > 0 && !remindersSent.has(reminderKey1h)) {
      return { type: '1h', key: reminderKey1h };
    }
    
    if (hoursUntilInterview <= 24 && hoursUntilInterview > 23 && !remindersSent.has(reminderKey24h)) {
      return { type: '24h', key: reminderKey24h };
    }
    
    return null;
  }, [remindersSent]);

  // Invia reminder per una candidatura
  const sendReminder = useCallback(async (application: any) => {
    if (!userProfile?.email || !userProfile?.name) {
      console.warn('⚠️ Email o nome utente non disponibili per il reminder');
      return false;
    }

    const appliedDate = application.appliedDate.toDate ? 
      application.appliedDate.toDate() : 
      new Date(application.appliedDate);
    
    const daysSinceApplication = differenceInDays(new Date(), appliedDate);

    try {
      console.log(`📧 Invio reminder per candidatura: ${application.companyName} (${daysSinceApplication} giorni)`);
      
      const reminderData = {
        to_name: userProfile.name,
        to_email: userProfile.email,
        company_name: application.companyName,
        position: application.position,
        applied_date: appliedDate.toLocaleDateString('it-IT'),
        days_since_application: daysSinceApplication,
        application_url: `https://job.studioquovadis.ch/applications/${application.id}`
      };
      
      // Usa solo Resend
      let success = false;
      if (resendEmailService.isServiceConfigured()) {
        success = await resendEmailService.sendApplicationReminder(reminderData);
      }

      if (success) {
        console.log(`✅ Reminder inviato per candidatura: ${application.companyName}`);
        // Marca come inviato
        const reminderKey = `${application.id}_reminder`;
        setRemindersSent(prev => new Set(Array.from(prev).concat([reminderKey])));
      }

      return success;
    } catch (error) {
      console.error('❌ Errore nell\'invio reminder:', error);
      return false;
    }
  }, [userProfile]);

  // Invia reminder per colloquio
  const sendInterviewReminder = useCallback(async (application: any, reminderInfo: { type: string, key: string }) => {
    if (!userProfile?.email || !userProfile?.name) {
      console.warn('⚠️ Email o nome utente non disponibili per il reminder colloquio');
      return false;
    }

    const interviewDate = application.interviewDate.toDate ? 
      application.interviewDate.toDate() : 
      new Date(application.interviewDate);

    try {
      console.log(`📧 Invio reminder colloquio ${reminderInfo.type} per: ${application.companyName}`);
      
      // Usa solo Resend
      let success = false;
      if (resendEmailService.isServiceConfigured()) {
        success = await resendEmailService.sendInterviewReminder({
          to_name: userProfile.name,
          to_email: userProfile.email,
          company_name: application.companyName,
          position: application.position,
          interview_date: interviewDate.toLocaleDateString('it-IT'),
          interview_time: interviewDate.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' }),
          interview_location: application.interviewLocation || 'Da confermare',
          interview_type: application.interviewType || 'In presenza'
        });
      }

      if (success) {
        console.log(`✅ Reminder colloquio ${reminderInfo.type} inviato per: ${application.companyName}`);
        // Marca come inviato
        setRemindersSent(prev => new Set(Array.from(prev).concat([reminderInfo.key])));
      }

      return success;
    } catch (error) {
      console.error('❌ Errore nell\'invio reminder colloquio:', error);
      return false;
    }
  }, [userProfile]);

  // Invia digest settimanale
  const sendWeeklyDigest = useCallback(async () => {
    if (!userProfile?.email || !userProfile?.name) {
      console.warn('⚠️ Email o nome utente non disponibili per il digest settimanale');
      return false;
    }

    try {
      const now = new Date();
      const weekStart = startOfWeek(now, { locale: it });
      const weekEnd = endOfWeek(now, { locale: it });
      
      const weekApplications = applications.filter(app => {
        const appDate = app.appliedDate?.toDate ? app.appliedDate.toDate() : new Date(app.appliedDate as any);
        return appDate >= weekStart && appDate <= weekEnd;
      });

      const newApplications = weekApplications.length;
      const interviewsScheduled = applications.filter(app => app.status === 'Colloquio programmato').length;
      const responsesReceived = applications.filter(app => 
        app.status === 'Accettato' || app.status === 'Rifiutato'
      ).length;

      console.log('📧 Invio digest settimanale...');
      
      const digestData = {
        to_name: userProfile.name,
        to_email: userProfile.email,
        week_start: format(weekStart, 'dd/MM/yyyy', { locale: it }),
        week_end: format(weekEnd, 'dd/MM/yyyy', { locale: it }),
        total_applications: applications.length,
        new_applications: newApplications,
        interviews_scheduled: interviewsScheduled,
        responses_received: responsesReceived,
        applications_url: 'https://job.studioquovadis.ch/applications'
      };
      
      // Usa solo Resend
      let success = false;
      if (resendEmailService.isServiceConfigured()) {
        success = await resendEmailService.sendWeeklyDigest(digestData);
      }

      if (success) {
        console.log('✅ Digest settimanale inviato con successo');
      }

      return success;
    } catch (error) {
      console.error('❌ Errore nell\'invio digest settimanale:', error);
      return false;
    }
  }, [applications, userProfile]);

  // Controlla e invia reminder per tutte le candidature che ne hanno bisogno
  const checkAndSendReminders = useCallback(async () => {
    // Usa solo Resend
    const isResendConfigured = resendEmailService.isServiceConfigured();
    
    if (!isResendConfigured) {
      console.warn('⚠️ Resend non configurato. Impossibile inviare reminder.');
      return;
    }

    console.log('🔍 Controllo reminder candidature...');
    
    const applicationsNeedingReminder = applications.filter(needsReminder);
    const applicationsNeedingInterviewReminder = applications
      .map(app => ({ app, reminderInfo: needsInterviewReminder(app) }))
      .filter(({ reminderInfo }) => reminderInfo !== null);
    
    if (applicationsNeedingReminder.length === 0 && applicationsNeedingInterviewReminder.length === 0) {
      console.log('✅ Nessuna candidatura ha bisogno di reminder');
      return;
    }

    console.log(`📧 ${applicationsNeedingReminder.length} candidature hanno bisogno di reminder`);
    console.log(`📧 ${applicationsNeedingInterviewReminder.length} colloqui hanno bisogno di reminder`);

    // Invia reminder candidature
    for (const application of applicationsNeedingReminder) {
      await sendReminder(application);
      // Piccola pausa tra le email per evitare rate limiting
      await new Promise(resolve => setTimeout(resolve, 1000));
    }

    // Invia reminder colloqui
    for (const { app, reminderInfo } of applicationsNeedingInterviewReminder) {
      if (reminderInfo && typeof reminderInfo === 'object' && 'type' in reminderInfo) {
        await sendInterviewReminder(app, reminderInfo);
        // Piccola pausa tra le email per evitare rate limiting
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
    }

    setLastReminderCheck(new Date());
  }, [applications, needsReminder, needsInterviewReminder, sendReminder, sendInterviewReminder]);

  // Controlla reminder ogni ora (più flessibile)
  useEffect(() => {
    const checkReminders = () => {
      const now = new Date();
      const hoursSinceLastCheck = (now.getTime() - lastReminderCheck.getTime()) / (1000 * 60 * 60);
      
      // Controlla ogni ora se ci sono candidature che necessitano reminder
      if (hoursSinceLastCheck >= 1) {
        console.log('⏰ Controllo periodico reminder...');
        checkAndSendReminders();
      }
    };

    // Controlla ogni 15 minuti
    const interval = setInterval(checkReminders, 15 * 60 * 1000);
    
    return () => clearInterval(interval);
  }, [checkAndSendReminders, lastReminderCheck]);

  // Controlla reminder al caricamento dell'app
  useEffect(() => {
    // Controlla solo se l'utente ha abilitato le notifiche email
    if (userProfile?.preferences?.emailNotifications) {
      console.log('🚀 Controllo iniziale reminder...');
      checkAndSendReminders();
    }
  }, [checkAndSendReminders, userProfile?.preferences?.emailNotifications]);

  // Invia digest settimanale ogni domenica alle 9:00
  useEffect(() => {
    const checkWeeklyDigest = () => {
      const now = new Date();
      const isSunday = now.getDay() === 0; // 0 = domenica
      const is9AM = now.getHours() === 9 && now.getMinutes() === 0;
      
      if (isSunday && is9AM && userProfile?.preferences?.emailNotifications) {
        console.log('📅 Invio digest settimanale...');
        sendWeeklyDigest();
      }
    };

    // Controlla ogni minuto
    const interval = setInterval(checkWeeklyDigest, 60 * 1000);
    
    return () => clearInterval(interval);
  }, [sendWeeklyDigest, userProfile?.preferences?.emailNotifications]);

  return {
    needsReminder,
    needsInterviewReminder,
    sendReminder,
    sendInterviewReminder,
    sendWeeklyDigest,
    checkAndSendReminders,
    applicationsNeedingReminder: applications.filter(needsReminder),
    applicationsNeedingInterviewReminder: applications
      .map(app => ({ app, reminderInfo: needsInterviewReminder(app) }))
      .filter(({ reminderInfo }) => reminderInfo !== null),
    lastReminderCheck,
    remindersSent: Array.from(remindersSent)
  };
}; 